import React, { useState } from "react";
import { toResult, type Result, type CheckStatus } from "@sequesign/verifier-browser";

import { bundleToLoadedReceipt } from "../load/bundle.js";
import { runDemoVerification } from "../verify/run-demo.js";
import { runSerialized } from "../verify/serialize.js";

// A distinct Overview LAYOUT for the delegated-document-work demo: a matter-file
// timeline whose steps (drafted -> cite-checked -> filed -> docketed) are the
// four actions of ONE sealed execution record. Verification is honest to the
// protocol: the whole receipt is verified as a unit (a chain cannot be verified
// one action at a time), so one "Verify the execution record" click checks every
// step together and lights them up. A toggle switches between two recorded
// receipts — a conformant filing and one filed in a court off the mandate's
// allowlist. All copy + the scenario→bundle wiring come from props.

export interface TimelineStep {
  // Which built-in glyph to draw (see ICONS).
  icon: "draft" | "cite" | "file" | "docket";
  step: string;
  title: string;
  detail: string;
}

export interface MatterScenario {
  key: string;
  toggleLabel: string;
  // Key into the `bundles` map: the recorded execution record this shows.
  bundleId: string;
  steps: TimelineStep[];
  // The bottom "execution record" summary, shown before verification as the
  // promise and refined by the verdict after.
  execTitle: string;
  execDetail: string;
  // false when this scenario's filing is off the mandate (conformant:false).
  expectConformant: boolean;
}

export interface MatterTimelineConfig {
  eyebrow: string;
  headline: string;
  sub: string;
  matterTitle: string;
  matterSub: string;
  mandate: {
    docTypesLabel: string;
    docTypes: string;
    courtsLabel: string;
    courts: string;
  };
  scenarios: MatterScenario[];
  verifyLabel: string;
  verifyingLabel: string;
  reverifyLabel: string;
  browserNote: string;
}

export interface MatterCta {
  label: string;
  sub: string;
  onClick: () => void;
}

const STATUS_MARK: Record<CheckStatus, string> = {
  pass: "✓",
  fail: "✕",
  partial: "◐",
  not_evaluated: "–"
};

const ICONS: Record<TimelineStep["icon"], React.ReactNode> = {
  draft: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 3h8l4 4v14H6z" />
      <path d="M14 3v4h4" />
      <path d="M9 13h6M9 17h4" />
    </svg>
  ),
  cite: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6h11M4 12h11M4 18h7" />
      <path d="M17 15l2 2 4-4" />
    </svg>
  ),
  file: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3 7l9-4 9 4-9 4-9-4z" />
      <path d="M3 7v6l9 4 9-4V7" />
    </svg>
  ),
  docket: (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="9" />
      <path d="M8 12l3 3 5-6" />
    </svg>
  )
};

export function MatterTimeline({
  config,
  bundles,
  ctas
}: {
  config: MatterTimelineConfig;
  bundles: Record<string, Record<string, string>>;
  ctas: MatterCta[];
}) {
  const [active, setActive] = useState<string>(config.scenarios[0]?.key ?? "");
  const [results, setResults] = useState<Record<string, Result>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<boolean>(false);

  const scenario = config.scenarios.find((s) => s.key === active) ?? config.scenarios[0];
  const result = results[scenario.bundleId] ?? null;
  const error = errors[scenario.bundleId] || null;
  const verified = result !== null;

  async function verify(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setErrors((e) => ({ ...e, [scenario.bundleId]: "" }));
    try {
      const bundle = bundles[scenario.bundleId];
      if (!bundle) throw new Error(`No recorded bundle for ${scenario.bundleId}`);
      const loaded = bundleToLoadedReceipt(scenario.bundleId, bundle);
      const started = performance.now();
      // Serialized process-wide (shared virtual FS): never overlap another run.
      const { report } = await runSerialized(() => runDemoVerification(loaded));
      const totalMs = performance.now() - started;
      const r = toResult(report, { totalMs, verifierVersion: "demo", commit: "demo" });
      setResults((prev) => ({ ...prev, [scenario.bundleId]: r }));
    } catch (err) {
      setErrors((e) => ({
        ...e,
        [scenario.bundleId]: err instanceof Error ? err.message : String(err)
      }));
    } finally {
      setBusy(false);
    }
  }

  // Node state: before verification, neutral; after a valid verification, every
  // step is confirmed (the whole chain verified). A non-conformant record is
  // still authentic, so steps still show confirmed — the verdict box carries the
  // conformance story.
  const nodeState = verified && result?.valid ? "done" : "idle";

  return (
    <section className="matter">
      <div className="matter-head-copy">
        <div className="matter-eyebrow">{config.eyebrow}</div>
        <h1 className="matter-headline">{config.headline}</h1>
        <p className="matter-sub">{config.sub}</p>
      </div>

      <div className="matter-file">
        <div className="matter-meta">
          <div>
            <div className="matter-title">{config.matterTitle}</div>
            <div className="matter-metasub">{config.matterSub}</div>
          </div>
          <div className="matter-mandate">
            <div className="mfield">
              <div className="mfield-k">{config.mandate.docTypesLabel}</div>
              <div className="mfield-v">{config.mandate.docTypes}</div>
            </div>
            <div className="mfield">
              <div className="mfield-k">{config.mandate.courtsLabel}</div>
              <div className="mfield-v">{config.mandate.courts}</div>
            </div>
          </div>
        </div>

        <div className="matter-toggle" role="tablist" aria-label="Filing scenario">
          {config.scenarios.map((s) => (
            <button
              key={s.key}
              role="tab"
              aria-selected={s.key === active}
              className={
                "matter-toggle-btn" + (s.key === active ? " matter-toggle-btn-active" : "")
              }
              onClick={() => setActive(s.key)}
              disabled={busy}
            >
              {s.toggleLabel}
            </button>
          ))}
        </div>

        <div className="timeline">
          {scenario.steps.map((step, i) => (
            <div
              key={i}
              className={
                "tl-node" +
                (nodeState === "done"
                  ? scenario.expectConformant
                    ? " tl-node-done"
                    : " tl-node-warn"
                  : "")
              }
            >
              <span className="tl-dot" aria-hidden="true">
                {ICONS[step.icon]}
              </span>
              <div className="tl-body">
                <div className="tl-step">{step.step}</div>
                <div className="tl-title">{step.title}</div>
                <div className="tl-detail">{step.detail}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="matter-verify">
          <button className="matter-verify-btn" onClick={verify} disabled={busy}>
            {busy ? config.verifyingLabel : verified ? config.reverifyLabel : config.verifyLabel}
          </button>
        </div>

        {error && <div className="matter-error">Verification error: {error}</div>}

        <ExecRecord scenario={scenario} result={result} />
      </div>

      <p className="matter-browser-note">{config.browserNote}</p>

      <div className="matter-ctas">
        {ctas.map((cta, i) => (
          <button className="matter-cta" key={i} onClick={cta.onClick}>
            <span className="matter-cta-label">{cta.label}</span>
            <span className="matter-cta-sub">{cta.sub}</span>
          </button>
        ))}
      </div>
    </section>
  );
}

function ExecRecord({ scenario, result }: { scenario: MatterScenario; result: Result | null }) {
  // Before verification: the promise. After: the verdict.
  if (!result) {
    return (
      <div className="exec exec-idle">
        <div className="exec-head">
          <span className="exec-mark" aria-hidden="true">
            •
          </span>
          <span className="exec-title">{scenario.execTitle}</span>
        </div>
        <p className="exec-detail">{scenario.execDetail}</p>
      </div>
    );
  }

  const valid = result.valid;
  const conformant = result.conformant;
  const cls = !valid ? "exec-invalid" : conformant === false ? "exec-warn" : "exec-valid";
  const title = !valid
    ? "Verification failed"
    : conformant === false
      ? "Execution record verified: authentic, but NOT conformant"
      : "Execution record verified: conformant";
  const detail = !valid
    ? `Rejected: ${result.reason ?? "see the failed check below."}`
    : conformant === false
      ? "Every step is still sealed, witnessed, and authentic, but the receipt honestly records that the filing went to a court off the mandate's allowlist (conformant: false). The mandate caught it; the record didn't hide it."
      : "Every step is sealed, witnessed, and bound to the matter's mandate, and the filing conforms to the allowed courts. Anyone can verify the whole record offline.";

  return (
    <div className={"exec " + cls}>
      <div className="exec-head">
        <span className="exec-mark" aria-hidden="true">
          {valid ? "✓" : "✕"}
        </span>
        <span className="exec-title">{title}</span>
      </div>
      <p className="exec-detail">{detail}</p>

      {result.trustAnchorMode === "self" && (
        <p className="exec-anchor">
          Integrity self-check: verified against the witness key carried in this recorded receipt,
          not an independent third-party anchor. The <strong>Run live</strong> tab anchors to the
          witness's published key for a genuine third-party check.
        </p>
      )}

      <details className="exec-checks">
        <summary>Show the {result.checks.length} checks the verifier ran</summary>
        <ul className="exec-check-list">
          {result.checks.map((c) => (
            <li key={c.id} className={`exec-check exec-check-${c.status}`}>
              <span className="exec-check-mark" aria-hidden="true">
                {STATUS_MARK[c.status]}
              </span>
              <span className="exec-check-detail">{c.detail}</span>
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
